import { motion } from "framer-motion";
import { ArrowRight, BrainCircuit, Radio, Database, Layers, TrendingUp } from "lucide-react";
import { useLang } from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadForm } from "@/components/LeadForm";
import WorldHero from "@/components/WorldHero";
import { Reveal } from "@/components/Motion";
import { Link } from "react-router-dom";

const SERVICE_LINKS = [
  "/martech/services/bigquery-dashboarding",
  "/martech/services/ga4-digital-analytics",
  "/martech/services/advanced-modeling",
  "/martech/services/advanced-modeling",
  "/martech/services/crm-lifecycle",
];

const INPUT_ICONS = [Radio, Database, Layers, BrainCircuit];
const INPUT_TOPS = ["14%", "30%", "46%", "62%"];
const LOOP_POS = [
  { top: "6%", left: "67%" },
  { top: "45%", left: "92%" },
  { top: "79%", left: "83%" },
  { top: "79%", left: "50%" },
  { top: "27%", left: "42%" },
];
const INPUT_PATHS = [
  "M150 62 C 195 62, 208 112, 229 134",
  "M150 132 C 192 132, 202 158, 214 170",
  "M150 202 C 180 202, 192 203, 205 205",
  "M150 272 C 192 272, 202 250, 214 240",
];
const INPUT_DOTS = [[229, 134], [214, 170], [205, 205], [214, 240]];

const GrowthDiagram = ({ m }) => (
  <div data-testid="growth-banner-diagram" className="relative w-full rounded-2xl p-4 sm:p-7" style={{ background: "linear-gradient(135deg, rgba(202,59,66,0.07), rgba(67,97,238,0.07))" }}>
    <div className="relative w-full aspect-[13/11]">
      <svg viewBox="0 0 520 440" className="absolute inset-0 h-full w-full" fill="none" aria-hidden="true">
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "350px 205px" }}
        >
          <circle cx="350" cy="205" r="145" stroke="#ca3b42" strokeOpacity="0.4" strokeWidth="1.5" strokeDasharray="2 9" />
          <circle cx="350" cy="60" r="3.5" fill="#ca3b42" />
          <circle cx="495" cy="205" r="2.5" fill="#4361ee" />
        </motion.g>
        <circle cx="350" cy="205" r="108" stroke="#353535" strokeOpacity="0.08" strokeWidth="1.5" />
        {INPUT_PATHS.map((d, i) => (
          <path key={i} d={d} stroke="#353535" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="1 6" strokeLinecap="round" />
        ))}
        {INPUT_DOTS.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3" fill="#ca3b42" />
        ))}
        <path d="M350 350 L350 392" stroke="#ca3b42" strokeOpacity="0.5" strokeWidth="1.5" strokeDasharray="1 6" strokeLinecap="round" />
        <circle cx="350" cy="350" r="3.5" fill="#ca3b42" />
      </svg>
      <div className="absolute flex aspect-square w-[27%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-1.5 rounded-full bg-white text-center shadow-[0_18px_45px_rgba(53,53,53,0.12)]" style={{ top: "46.6%", left: "67.3%" }}>
        <TrendingUp size={16} className="text-[#ca3b42]" />
        <span className="px-2 font-cabinet text-[11px] sm:text-xs font-bold leading-tight">{m.bannerCenter}</span>
      </div>
      {m.bannerLoop.map((l, i) => (
        <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap font-jbmono text-[8px] sm:text-[9px] uppercase tracking-[0.18em] text-[#737373]" style={LOOP_POS[i]}>
          {l}
        </span>
      ))}
      {m.bannerInputs.map((inp, i) => {
        const Icon = INPUT_ICONS[i];
        return (
          <span key={i} className="absolute left-0 flex -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 bg-white px-3 py-1.5 font-jakarta text-[10px] font-semibold text-[#353535] shadow-sm" style={{ top: INPUT_TOPS[i] }}>
            <Icon size={11} className="text-[#4361ee]" /> {inp}
          </span>
        );
      })}
      <span className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#353535] px-4 py-2 font-jakarta text-[10px] font-semibold text-white" style={{ left: "67.3%" }}>
        {m.bannerOutput}
      </span>
    </div>
  </div>
);

const MartechWorld = () => {
  const { t } = useLang();

  return (
    <div data-testid="martech-world" className="bg-[#FAFAFD] text-[#353535]">
      <Navbar accent="#ca3b42" />

      <WorldHero
        testid="martech-hero"
        accent="#ca3b42"
        kicker={t.martech.kicker}
        l1={t.martech.heroL1}
        l2={t.martech.heroL2}
        sub={t.martech.heroSub}
        cta1={{ label: t.martech.heroCta1, to: "/contact?unit=martech" }}
        cta2={{ label: t.martech.heroCta2, to: "#martech-servicios" }}
      >
        <div className="w-full shrink-0 lg:w-[124%] lg:max-w-[760px]">
          <GrowthDiagram m={t.martech} />
        </div>
      </WorldHero>

      <section id="martech-servicios" className="px-5 sm:px-8 py-20">
        <div className="mx-auto max-w-[1400px] grid gap-4 lg:grid-cols-2">
          {t.martech.services5.map((s, i) => {
            const featured = i === 2;
            return (
              <Reveal key={i} delay={i * 0.06} className={featured ? "lg:col-span-2" : ""}>
                <Link
                  to={SERVICE_LINKS[i]}
                  data-testid={`martech-service-${i}`}
                  className={`group block h-full rounded-3xl p-8 sm:p-10 transition-all duration-500 hover:-translate-y-1.5 ${
                    featured
                      ? "border-2 border-[#ca3b42] bg-white shadow-[0_30px_80px_rgba(202,59,66,0.14)] hover:shadow-[0_40px_90px_rgba(202,59,66,0.22)]"
                      : "border border-black/8 bg-white hover:shadow-[0_30px_70px_rgba(53,53,53,0.10)]"
                  }`}
                >
                  <div className={featured ? "lg:flex lg:items-center lg:gap-14" : ""}>
                    <div className={featured ? "lg:w-2/5 lg:shrink-0" : ""}>
                      <span className={`font-jbmono text-xs ${featured ? "text-[#ca3b42]" : "text-[#999999]"}`}>({s.n})</span>
                      <h2 className={`mt-3 font-cabinet font-bold tracking-tight leading-tight ${featured ? "text-2xl sm:text-4xl" : "text-2xl"}`}>{s.name}</h2>
                      <p className={`mt-2 font-jakarta font-semibold ${featured ? "text-base sm:text-lg text-[#ca3b42]" : "text-base text-[#353535]"}`}>{s.tagline}</p>
                      <p className="mt-3 text-sm text-[#999999] font-jakarta leading-relaxed">{s.desc}</p>
                    </div>
                    <div className={`mt-6 flex flex-wrap content-start gap-2 ${featured ? "lg:mt-0 lg:flex-1" : ""}`}>
                      {s.tags.map((tag, j) => (
                        <span
                          key={j}
                          className={`rounded-full px-3 py-1.5 text-[11px] font-jakarta font-medium ${
                            featured
                              ? "border border-[#ca3b42]/25 bg-[#ca3b42]/8 text-[#ca3b42]"
                              : "border border-black/8 bg-[#FAFAFD] text-[#353535]/75"
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 sm:py-28 bg-[#353535] text-white">
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <h2 className="font-clash text-3xl sm:text-5xl font-semibold leading-tight">{t.martech.ctaTitle}</h2>
            <p className="mt-5 text-white/75 font-jakarta max-w-md">{t.martech.ctaBody}</p>
            <Link data-testid="martech-cta-master" to="/master" className="group mt-8 inline-flex items-center gap-2 font-jbmono text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white transition-colors">
              Analytics 4 People <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm defaultUnit="martech" accent="#ca3b42" />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MartechWorld;
